'use client';

import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useRouter } from 'next/navigation';
import { useState, type MouseEvent } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { black, slate } from '@/lib/colours';
import { UserRole } from '@/lib/types';
import { DocumentTypeCode } from '@/apiClient';
import { usePrivate } from '@/components/private/PrivateContext';
import { MenuIcon } from '@/components/icons';
import {
  Box,
  Divider,
  DocumentAvatar,
  Menu,
  MenuItem,
  Typography,
} from '@/primitives';
import { OrganisationSelectorModal } from './organisation-selector/OrganisationSelectorModal';
import { userAvatarMessages } from './messages';

export function UserAvatar() {
  const router = useRouter();
  const { instance, inProgress } = useMsal();
  const {
    b2cFirstName,
    userProfile,
    userOrganisations,
    selectedOrganisationId,
  } = usePrivate();

  // Get feature flag for authentication tests
  const { enableAuthTests = false } = useFlags();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const userOrganisationsFound = userOrganisations?.find(
    (o) => o.organisationId === selectedOrganisationId,
  );

  const [organisationSelectorOpen, setOrganisationSelectorOpen] =
    useState(false);

  const handleClickUserProfileButton = (event: MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleClickAccount = () => {
    setAnchorEl(null);
    router.push('/account');
  };

  const handleClickSwitchSociety = () => {
    setAnchorEl(null);
    setOrganisationSelectorOpen(true);
    handleCloseMenu();
  };

  const handleClickTests = () => {
    setAnchorEl(null);
    router.push('/tests');
  };

  const handleClickLogout = () => {
    if (inProgress === InteractionStatus.None) {
      setAnchorEl(null);

      const currentAccount = instance.getActiveAccount();
      let provider = 'default';
      if (currentAccount && currentAccount.idTokenClaims) {
        // Access the idTokenClaims object
        const claims = currentAccount.idTokenClaims;

        // --- Check the 'idp' claim ---
        const idp = claims.idp;

        if (idp && idp.toLowerCase() === 'google.com') {
          provider = 'google';
        }
      }

      const queryParams = new URLSearchParams({
        provider: provider,
      });

      // Clear selected org id when logout
      window.localStorage.clear();
      instance.logoutRedirect({
        postLogoutRedirectUri: `/post-logout?${queryParams.toString()}`,
      });
    }
  };

  const getInitials = () => {
    if (userProfile?.firstName) {
      return (
        (userProfile.firstName?.at(0) || '') +
        (userProfile.lastName?.at(0) || '')
      );
    }
    return b2cFirstName?.at(0) || '';
  };

  const getName = () => {
    if (userProfile?.firstName) {
      return (
        userProfile.firstName +
        (userProfile.lastName ? ` ${userProfile.lastName}` : '')
      );
    }
    return b2cFirstName;
  };

  return (
    <>
      <Box
        aria-controls={open ? 'user-profile-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        id="user-profile-button"
        onClick={handleClickUserProfileButton}
        sx={{
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
        }}
      >
        <DocumentAvatar
          documentTypeCode={DocumentTypeCode.Avatar}
          documentEntityId={userProfile?.userProfileEntityId}
        >
          {getInitials()}
        </DocumentAvatar>
        <Box
          sx={{ display: 'flex', flexDirection: 'column', paddingLeft: '12px' }}
        >
          <Typography variant="titlemedium" color={slate[900]}>
            {getName()}
          </Typography>
          {userOrganisationsFound && (
            <Typography variant="bodysmall" color={slate[600]}>
              {UserRole[userOrganisationsFound.roleId!]}
            </Typography>
          )}
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <MenuIcon sx={{ fontSize: '48px', color: black[900] }} />
      </Box>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        id="user-profile-menu"
        onClose={handleCloseMenu}
        MenuListProps={{
          'aria-labelledby': 'user-profile-button',
        }}
        open={open}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <MenuItem onClick={handleClickAccount}>
          {userAvatarMessages['userAvatar.menu.account.label']}
        </MenuItem>
        {userOrganisationsFound?.roleId === UserRole.Admin &&
          enableAuthTests && (
            <MenuItem onClick={handleClickTests}>
              {userAvatarMessages['userAvatar.menu.tests.label']}
            </MenuItem>
          )}
        {userOrganisationsFound?.roleId === UserRole.Admin &&
          enableAuthTests && <Divider />}
        {userOrganisationsFound &&
          userOrganisations &&
          userOrganisations.length > 1 && (
            <MenuItem onClick={handleClickSwitchSociety}>
              {userAvatarMessages['userAvatar.menu.switchSociety.label']}
            </MenuItem>
          )}
        {userOrganisationsFound &&
          userOrganisations &&
          userOrganisations.length > 1 && <Divider />}
        <MenuItem onClick={handleClickLogout}>
          {userAvatarMessages['userAvatar.menu.logout.label']}
        </MenuItem>
      </Menu>
      <OrganisationSelectorModal
        open={organisationSelectorOpen}
        onClose={() => setOrganisationSelectorOpen(false)}
      />
    </>
  );
}
